import { EntityState, SocialEntityState } from '../../../types';
import {
  BlockWords,
  ClearToken,
  FSAInterface,
  RemoveOrgUser,
  RequireWords,
  SetEntity,
  SetEntityMembers,
  SetMemberRole,
  SetNetworkCommentPostOpt,
  SetSubscribedInstagramMentions,
  SetToken,
  ToggleWordBlacklist,
  UnblockWord,
} from '../../actions';
import uniqBy from 'lodash/fp/uniqBy';
import { OrgMember } from '../../../models/Org';
import get from 'lodash/fp/get';
import uniq from 'lodash/fp/uniq';
import set from 'lodash/fp/set';
import { Services } from '../../../constants';

export function setEntity(state: EntityState, action: FSAInterface<SetEntity['payload']>) {
  const { payload = {} } = action;
  const entityInfo = { ...payload };
  delete entityInfo['metadata'];
  return { ...state, ...{ ...entityInfo, ...payload['metadata'] } };
}

export function updateEntity(state: EntityState, action: FSAInterface<any>) {
  const { payload } = action;
  const formattedPayload = { ...payload };
  delete formattedPayload['members'];
  return { ...state, ...formattedPayload };
}

export function setEntityMembers(state: EntityState, action: FSAInterface<SetEntityMembers['payload']>) {
  return { ...state, members: action.payload };
}

export function addNewEntityMembers(state: EntityState, action: any) {
  const invitedMembers = action.members;
  // Need to bail if success array is empty, the api could return no successful invites.
  if (!invitedMembers.length) {
    return { ...state };
  }
  const { members } = state;
  const newMembers = uniqBy('_id', [...members, ...invitedMembers]);
  return { ...state, members: newMembers };
}

export function removeEntityUser(state: EntityState, action: FSAInterface<RemoveOrgUser['payload']>) {
  const { members } = state;
  const newMembers = members.filter((member: OrgMember) => member._id !== (action.payload?._id ?? ''));
  return { ...state, members: newMembers };
}

export function setEntityMemberRole(state: EntityState, action: FSAInterface<SetMemberRole['payload']>) {
  const { payload } = action;
  if (payload && payload?.members) {
    const updatedId = Object.keys(payload?.members)[0];
    const updatedMember = payload?.members[updatedId];
    const { members } = state;
    const newMembers = members.map((member: OrgMember) => {
      if (member._id === updatedId) {
        return { ...member, role: updatedMember.role };
      }

      return member;
    });

    return { ...state, members: newMembers };
  }

  return state;
}

export function setEntityMemberRoleOptimistic(state: EntityState, action: any) {
  const { payload } = action;
  const newMembers = state.members.map((member: OrgMember) => {
    if (member._id === payload.memberId) {
      return { ...member, role: payload.role };
    }

    return member;
  });
  return { ...state, members: newMembers };
}

export function setEntityMeta(state: EntityState, action: FSAInterface<{ metadata: any }>) {
  const meta = get('payload.metadata', action);
  return { ...state, ...meta };
}

export function entityBlockWord(state: EntityState, action: FSAInterface<BlockWords['payload']>) {
  const currentBlocked = state.blockedWords ? [...state.blockedWords] : [];
  const newBlockedWords = uniq([...currentBlocked, ...(action?.payload?.blockedWords || [])]);
  return { ...state, blockedWords: [...newBlockedWords] };
}

export function entityUnblockWord(state: EntityState, action: FSAInterface<UnblockWord['payload']>) {
  const currentBlocked = [...state.blockedWords];
  const newBlockedWords = currentBlocked.filter((word: string) => word !== get('payload.unblockedWord', action));
  return { ...state, blockedWords: [...newBlockedWords] };
}

export function entityToggleWordBlackList(state: EntityState, action: FSAInterface<ToggleWordBlacklist['payload']>) {
  const active = get('payload.active', action);
  return { ...state, wordBlacklist: active };
}

export function entityUnblockUserName(state: EntityState, action: any) {
  const { socialService, username } = action;
  const currentEntity = { ...state };
  currentEntity.blocked[socialService] = currentEntity.blocked[socialService].filter((user: string) => user !== username);
  return { ...state, blocked: { ...currentEntity.blocked } };
}

export function entityBlockUsername(state: EntityState, action: any) {
  const { socialService, username } = action;
  const currentBoard = { ...state };
  if (!currentBoard.blocked[socialService]) {
    currentBoard.blocked[socialService] = [];
  }
  currentBoard.blocked[socialService].push(username);
  return { ...state, blocked: { ...currentBoard.blocked } };
}

export function setEntityAssociatedFBPage(state: SocialEntityState, action: any) {
  const { socialNetworks } = state;
  if (!socialNetworks) {
    return;
  }
  const facebook = { ...(socialNetworks.facebook || {}) };
  facebook.associatedPage = get('payload.socialNetworks.facebook.associatedPage', action) || get('payload.pageId', action);
  facebook.instagram = get('payload.socialNetworks.facebook.instagram', action);
  facebook.subscribedInstagramMentions = get('payload.socialNetworks.facebook.subscribedInstagramMentions', action);
  return { ...state, socialNetworks: { ...socialNetworks, facebook } };
}

export function setEntitySubscribedInstagramMentions(
  state: SocialEntityState,
  action: FSAInterface<SetSubscribedInstagramMentions['payload']>,
) {
  const { socialNetworks } = state;
  if (!socialNetworks) {
    return;
  }
  const facebook = { ...(socialNetworks.facebook || {}) };
  facebook.subscribedInstagramMentions = get('payload.subscribe', action);
  return { ...state, socialNetworks: { ...socialNetworks, facebook } };
}

export function clearEntitySocialToken(state: SocialEntityState, action: FSAInterface<ClearToken['payload']>) {
  const currentNetworks = { ...state.socialNetworks };
  if (action.payload) {
    delete currentNetworks[action.payload.network];
  }
  return { ...state, socialNetworks: { ...currentNetworks } };
}

export function setEntitySocialToken(state: SocialEntityState, action: FSAInterface<SetToken['payload']>) {
  const currentNetworks = { ...state.socialNetworks };
  const newNetwork = get('payload.socialNetworks', action);
  return { ...state, socialNetworks: { ...currentNetworks, ...newNetwork } };
}

export function setCommentPostForNetworkOpt(
  state: SocialEntityState,
  action: FSAInterface<SetNetworkCommentPostOpt['payload']>,
) {
  const { network, postId, postLink } = action?.payload || {};
  const finalNetwork = network === Services.INSTAGRAM ? `facebook.${network}` : network;
  const newSelectedPost = {
    postId,
    postLink,
  };
  const newSocialNetworks = set(`${finalNetwork}.selectedPost`, newSelectedPost, { ...state?.socialNetworks });
  return { ...state, socialNetworks: newSocialNetworks };
}

export function setCommentPostForNetwork(state: SocialEntityState, action: any) {
  const { network, socialNetworks } = action?.payload;
  const finalNetwork = network === Services.INSTAGRAM ? Services.FACEBOOK : network;
  const newSocialNetworks = set(finalNetwork, socialNetworks[finalNetwork], { ...state?.socialNetworks });
  return { ...state, socialNetworks: newSocialNetworks };
}

export function setCustomCSS(state: EntityState, action: FSAInterface<{ metadata: any }>) {
  const { payload } = action;

  return { ...state, customCSS: payload?.metadata?.customCSS };
}

export function entityAddRequiredWords(state: EntityState, action: FSAInterface<RequireWords['payload']>) {
  const { payload = { requiredWords: [] } } = action;
  const currentRequired = state.requiredWords ? [...state.requiredWords] : [];
  const newRequiredWords = uniq([...currentRequired, ...payload?.requiredWords]);
  return { ...state, requiredWords: [...newRequiredWords] };
}

export function entityRemoveRequiredWord(state: EntityState, action: any) {
  const currentRequired = [...state.requiredWords];
  const newRequiredWords = currentRequired.filter((word: string) => word !== get('meta.removedWord', action));
  return { ...state, requiredWords: [...newRequiredWords] };
}
